import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Markdown from "@/components/Markdown";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
  Sparkles, Coins, Download, Share2, Globe, Wand2,
  BookOpen, FileText, ListChecks, GraduationCap, NotebookPen,
  Layers, MessageSquareHeart, Target, ClipboardCheck, Repeat, Loader2,
} from "lucide-react";

/* ============================================================================
 *  Full Teaching Kit Generator — 15 TeeBee Coins
 *  Generates 14 coherent connected outputs from a single topic.
 * ========================================================================= */

const SECTION_META = [
  { key: "learning_objectives",   label: "Learning Objectives",     icon: Target },
  { key: "lesson_plan",           label: "Lesson Plan",             icon: BookOpen },
  { key: "classroom_explanation", label: "Classroom Explanation",   icon: MessageSquareHeart },
  { key: "presentation_outline",  label: "Presentation Outline",    icon: Layers },
  { key: "worksheet",             label: "Worksheet",               icon: FileText },
  { key: "quiz",                  label: "Quiz",                    icon: ClipboardCheck },
  { key: "mcqs",                  label: "MCQs",                    icon: ListChecks },
  { key: "homework",              label: "Homework",                icon: NotebookPen },
  { key: "flashcards",            label: "Flashcards",              icon: GraduationCap },
  { key: "parent_note",           label: "Parent Note",             icon: MessageSquareHeart },
  { key: "assessment_rubric",     label: "Assessment Rubric",       icon: ClipboardCheck },
  { key: "recap_activity",        label: "Recap Activity",          icon: Repeat },
  { key: "exit_ticket",           label: "Exit Ticket",             icon: FileText },
  { key: "teacher_notes",         label: "Teacher Notes",           icon: NotebookPen },
];

const GRADES = ["Pre-K", "KG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "University"];
const SUBJECTS = ["Math", "Science", "Physics", "Chemistry", "Biology", "English", "Hindi", "History", "Geography", "Commerce", "Computer Science", "Art", "Music"];
const CURRICULA = ["CBSE", "ICSE", "IB", "Cambridge", "State Board", "US (Common Core)", "UK National", "Other"];
const LANGUAGES = ["English", "Hindi", "Kannada", "Tamil", "Telugu", "Marathi", "Bengali", "Arabic", "French", "Spanish"];

export default function FullTeachingKit() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  return kitId ? <KitView kitId={kitId} /> : <KitGenerator onCreated={(id) => navigate(`/teach/kit/${id}`)} />;
}

/* ────────────────────────────── GENERATOR ────────────────────────────── */
function KitGenerator({ onCreated }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState("");
  const [grade, setGrade] = useState("8");
  const [subject, setSubject] = useState("Science");
  const [curriculum, setCurriculum] = useState("CBSE");
  const [language, setLanguage] = useState("English");
  const [duration, setDuration] = useState("45 minutes");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    api.get("/coins/wallet").then(({ data }) => setBalance(data.balance)).catch(() => {});
  }, []);

  const cost = 15;
  const insufficient = balance != null && balance < cost;

  const generate = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      // POST now returns immediately (<1s) with kit_id + status="generating"
      const { data } = await api.post("/teaching-kit/generate", {
        topic, grade, subject, curriculum, language, duration,
      });
      toast.info("Generating your Full Teaching Kit…", {
        description: "We'll let you know the moment it's ready.",
      });
      // Navigate to the kit page — it polls until status === "ready".
      onCreated(data.kit_id);
      return;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not start generation — try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Full Teaching Kit">
      <div className="max-w-4xl mx-auto" data-testid="kit-generator">
        {/* ─── Premium hero card ─── */}
        <div className="relative rounded-3xl border border-border bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 dark:from-violet-500/10 dark:via-card dark:to-fuchsia-500/10 p-7 sm:p-9 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-gradient-to-br from-violet-400/30 to-fuchsia-400/20 blur-3xl pointer-events-none" />
          <div className="relative">
            <span className="tb-chip-lavender text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-2.5 py-0.5">
              <Sparkles className="inline h-3 w-3 mr-1" /> TeeBee Signature
            </span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium">
              Full Teaching Kit
            </h1>
            <p className="mt-2 text-muted-foreground max-w-xl text-[15px] leading-relaxed">
              One topic → 14 connected outputs: lesson plan, MCQs, worksheet, rubric, flashcards, parent note and more.
              All consistent in difficulty, vocabulary, and pedagogy.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-[12px]">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-200 px-2.5 py-1 font-medium">
                <Coins className="h-3 w-3" /> Costs <strong>{cost} TeeBee Coins</strong>
              </span>
              <span className="text-muted-foreground">
                · Your balance: <strong className="text-foreground">{balance ?? "…"}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* ─── Form ─── */}
        <div className="mt-6 rounded-3xl border border-border bg-card p-6 space-y-5">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Topic / Chapter</Label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Photosynthesis · Trigonometric ratios · The French Revolution"
              className="mt-1.5 h-11 rounded-xl"
              data-testid="kit-topic-input"
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field label="Grade">
              <SelectField value={grade} onChange={setGrade} options={GRADES} testid="kit-grade" />
            </Field>
            <Field label="Subject">
              <SelectField value={subject} onChange={setSubject} options={SUBJECTS} testid="kit-subject" />
            </Field>
            <Field label="Curriculum">
              <SelectField value={curriculum} onChange={setCurriculum} options={CURRICULA} testid="kit-curriculum" />
            </Field>
            <Field label="Language">
              <SelectField value={language} onChange={setLanguage} options={LANGUAGES} testid="kit-language" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Duration">
              <Input value={duration} onChange={(e) => setDuration(e.target.value)} className="h-10 rounded-xl" />
            </Field>
            <div className="hidden sm:block" />
          </div>

          <div className="pt-2 flex flex-wrap gap-3 items-center">
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={busy || !topic.trim() || insufficient}
              size="lg"
              className="rounded-full h-12 px-6"
              data-testid="kit-generate-cta"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
              {busy ? "Generating 14 outputs…" : `Generate · ${cost} coins`}
            </Button>
            {insufficient && (
              <span className="text-[12px] text-amber-700 dark:text-amber-300">
                Not enough coins · <a href="/wallet" className="underline">recharge</a>
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">
              You&apos;ll see a confirmation before any coins are deducted.
            </span>
          </div>
        </div>

        {/* ─── Preview of all 14 sections ─── */}
        <div className="mt-7">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-muted-foreground mb-3">
            What you get
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {SECTION_META.map((s) => {
              const I = s.icon;
              return (
                <div key={s.key} className="flex items-center gap-2 rounded-xl border border-border bg-card p-2.5 text-xs">
                  <span className="inline-grid place-items-center h-7 w-7 rounded-lg bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-200 shrink-0">
                    <I className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-medium text-foreground/90 leading-tight">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Confirmation dialog ─── */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-3xl p-6 max-w-md w-full" data-testid="kit-confirm-modal">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-violet-700 dark:text-violet-300">Confirm generation</p>
            <h3 className="mt-2 font-display text-2xl tracking-tight">Full Teaching Kit</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              This will use <strong className="text-foreground">{cost} TeeBee Coins</strong> from your balance ({balance}).
            </p>
            <div className="mt-3 rounded-xl bg-muted p-3 text-[12px] text-foreground/80">
              <strong>{topic || "(topic missing)"}</strong> · {subject} · Grade {grade} · {curriculum} · {language}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              If any section fails to generate, the coins for those are auto-refunded.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button className="rounded-full" onClick={generate} data-testid="kit-confirm-go">Generate {cost} coins</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SelectField({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-xl border border-border bg-card px-3 text-sm hover:border-violet-400/40"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

/* ────────────────────────────── VIEWER ────────────────────────────── */
function KitView({ kitId }) {
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Background-task aware loader: polls every 2s until status === "ready"
  useEffect(() => {
    let alive = true;
    let timer = null;
    let attempts = 0;

    const announcedRef = { current: false };

    const tick = async () => {
      try {
        const { data } = await api.get(`/teaching-kit/${kitId}`);
        if (!alive) return;
        setKit(data);
        if (!activeTab) {
          const first = SECTION_META.find((s) => data.sections?.[s.key]);
          if (first) setActiveTab(first.key);
        }
        if (data.status === "ready") {
          if (!announcedRef.current) {
            announcedRef.current = true;
            const failed = (data.failed_sections || []).length;
            if (failed > 0) {
              toast.warning(`Generated with ${failed} section(s) refunded`, {
                description: `Used ${data.coin_cost} TeeBee Coins · refunded ${data.refunded}.`,
              });
            } else {
              toast.success(`Full Teaching Kit ready · ${data.coin_cost} TeeBee Coins used`, {
                description: "Saved to your Lesson Workspace.",
              });
            }
          }
          return;   // stop polling
        }
        if (data.status === "failed") {
          toast.error(data.error || "Generation failed — coins refunded.");
          return;   // stop polling
        }
        // still generating → poll again (cap ~3 min of polling)
        attempts += 1;
        if (attempts < 90) {
          timer = setTimeout(tick, 2000);
        } else {
          toast.error("Generation is taking longer than usual — please refresh in a minute.");
        }
      } catch (e) {
        if (e?.response?.status === 404) { setNotFound(true); return; }
        // transient — retry up to 5x with backoff
        attempts += 1;
        if (alive && attempts < 5) timer = setTimeout(tick, 3000);
      }
    };

    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitId]);

  const exportPdf = () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    window.open(`${BACKEND}/api/teaching-kit/${kitId}/pdf`, "_blank");
  };

  const shareKit = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: kit.title, url }); }
      catch (e) {
        // Swallow user-cancel ('AbortError'). Surface real errors via toast.
        if (e?.name !== "AbortError") toast.error("Sharing failed");
      }
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Kit link copied"); }
      catch { toast.error("Could not copy link"); }
    }
  };

  const togglePublish = async () => {
    setPublishBusy(true);
    try {
      const { data } = await api.post(`/teaching-kit/${kitId}/publish`);
      setKit((k) => ({ ...k, is_published: data.is_published }));
      toast.success(data.is_published ? "Kit published publicly" : "Kit unpublished");
    } catch {
      toast.error("Action failed");
    } finally {
      setPublishBusy(false);
    }
  };

  const goDifferentiate = async () => {
    setDiffBusy(true);
    try {
      // POST returns immediately with pack_id + status=generating; PackView polls.
      const { data } = await api.post("/differentiate/generate", { kit_id: kitId });
      toast.info("Generating 5 learner variants…", {
        description: "We'll let you know the moment it's ready.",
      });
      navigate(`/teach/pack/${data.pack_id}`);
      return;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not start differentiation.");
      }
    } finally {
      setDiffBusy(false);
    }
  };

  if (notFound) {
    return (
      <Layout title="Teaching Kit">
        <div className="max-w-md mx-auto text-center py-20" data-testid="kit-not-found">
          <p className="text-sm text-muted-foreground">This kit could not be found.</p>
          <Button onClick={() => navigate("/teach/kit")} className="mt-4 rounded-full">Build a new kit</Button>
        </div>
      </Layout>
    );
  }
  if (!kit) return <Layout title="Teaching Kit"><GeneratingState topic="" /></Layout>;

  if (kit.status === "generating") {
    return <Layout title={kit.title}><GeneratingState topic={kit.topic} sections={kit.sections || {}} /></Layout>;
  }
  if (kit.status === "failed") {
    return (
      <Layout title={kit.title}>
        <div className="max-w-md mx-auto text-center py-20" data-testid="kit-failed">
          <p className="text-sm text-foreground">{kit.error || "Generation failed — your coins were refunded."}</p>
          <Button onClick={() => navigate("/teach/kit")} className="mt-4 rounded-full">Try again</Button>
        </div>
      </Layout>
    );
  }

  const available = SECTION_META.filter((s) => kit.sections?.[s.key]);
  const activeSection = available.find((s) => s.key === activeTab);

  return (
    <Layout title={kit.title}>
      <div className="max-w-6xl mx-auto" data-testid="kit-view">
        {/* ─── Header band ─── */}
        <div className="rounded-3xl border border-border bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 dark:from-violet-500/10 dark:via-card dark:to-fuchsia-500/10 p-6 sm:p-8">
          <span className="tb-chip-lavender text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-2.5 py-0.5">
            <Sparkles className="inline h-3 w-3 mr-1" /> Full Teaching Kit
          </span>
          <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium">{kit.title}</h1>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
            {kit.subject && <Pill>{kit.subject}</Pill>}
            {kit.grade && <Pill>Grade {kit.grade}</Pill>}
            {kit.curriculum && <Pill>{kit.curriculum}</Pill>}
            {kit.language && <Pill>{kit.language}</Pill>}
            {kit.duration && <Pill>{kit.duration}</Pill>}
            <Pill>{kit.coin_cost} coins used</Pill>
            {kit.refunded > 0 && <Pill tone="amber">+{kit.refunded} refunded</Pill>}
          </div>

          {/* ─── Action bar ─── */}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={exportPdf} variant="outline" className="rounded-full" data-testid="kit-export-pdf">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export PDF
            </Button>
            <Button onClick={shareKit} variant="outline" className="rounded-full" data-testid="kit-share">
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
            </Button>
            <Button onClick={togglePublish} variant="outline" className="rounded-full" disabled={publishBusy} data-testid="kit-publish">
              <Globe className="h-3.5 w-3.5 mr-1.5" /> {kit.is_published ? "Unpublish" : "Publish"}
            </Button>
            <Button onClick={goDifferentiate} disabled={diffBusy} className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white" data-testid="kit-differentiate-cta">
              {diffBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Layers className="h-3.5 w-3.5 mr-1.5" />}
              {diffBusy ? "Generating 5 variants…" : "Differentiate · 10 coins"}
            </Button>
          </div>
        </div>

        {/* ─── Tabs + content ─── */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground mb-2">14 outputs</p>
              <nav className="grid grid-cols-2 lg:grid-cols-1 gap-1.5" data-testid="kit-tabs">
                {available.map((s) => {
                  const I = s.icon;
                  const active = activeTab === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setActiveTab(s.key)}
                      className={`flex items-center gap-2 text-left rounded-xl border px-2.5 py-2 text-[12px] transition-colors ${active
                        ? "bg-violet-600 text-white border-violet-600"
                        : "border-border bg-card hover:border-violet-400/60 text-foreground/85"}`}
                      data-testid={`kit-tab-${s.key}`}
                    >
                      <I className="h-3.5 w-3.5 shrink-0" />
                      <span className="font-medium leading-tight">{s.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          <article className="lg:col-span-9">
            {activeSection ? (
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid={`kit-section-${activeSection.key}`}>
                <h2 className="font-display text-2xl tracking-tight font-medium mb-4">{activeSection.label}</h2>
                <Markdown>{kit.sections[activeSection.key]}</Markdown>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Select a section from the left.</p>
            )}
          </article>
        </div>
      </div>
    </Layout>
  );
}

function Pill({ children, tone = "default" }) {
  const cls = tone === "amber"
    ? "bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-200"
    : "bg-muted text-foreground/80";
  return <span className={`rounded-full px-2 py-0.5 font-medium ${cls}`}>{children}</span>;
}

/* ────────────────────────────── GENERATING STATE ────────────────────────────── */
function GeneratingState({ topic, sections = {} }) {
  const total = SECTION_META.length;
  const done = SECTION_META.filter((s) => sections[s.key]).length;
  const pct = Math.round((done / total) * 100);
  return (
    <div className="max-w-3xl mx-auto" data-testid="kit-generating">
      <div className="relative rounded-3xl border border-border bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 dark:from-violet-500/10 dark:via-card dark:to-fuchsia-500/10 p-6 sm:p-9 overflow-hidden">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-gradient-to-br from-violet-400/30 to-fuchsia-400/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="tb-chip-lavender text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-2.5 py-0.5">
            <Sparkles className="inline h-3 w-3 mr-1" /> Building your kit
          </span>
          <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium leading-snug">
            Crafting <span className="text-primary">{topic || "your full teaching kit"}</span>…
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            TeeBee Intelligence is generating 14 connected outputs in parallel. Hang tight — this usually takes
            30 to 90 seconds. You can safely leave this page open or come back later from your library.
          </p>

          {/* Progress bar */}
          <div className="mt-5 rounded-full bg-muted h-2 overflow-hidden" aria-label="generation progress" data-testid="kit-progress">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-amber-400 transition-all duration-700"
              style={{ width: `${Math.max(pct, 6)}%` }}
            />
          </div>
          <p className="mt-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
            {done} of {total} ready
          </p>
        </div>
      </div>

      {/* Section checklist */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="kit-section-checklist">
        {SECTION_META.map((s) => {
          const ready = !!sections[s.key];
          const I = s.icon;
          return (
            <div
              key={s.key}
              className={`flex items-center gap-2 rounded-xl border p-2.5 text-xs transition ${
                ready
                  ? "border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-500/10 dark:border-emerald-500/30 text-emerald-900 dark:text-emerald-200"
                  : "border-border bg-card text-muted-foreground"
              }`}
              data-testid={`kit-pending-${s.key}`}
            >
              <span className={`inline-grid place-items-center h-7 w-7 rounded-lg shrink-0 ${
                ready ? "bg-emerald-100 dark:bg-emerald-500/20" : "bg-muted"
              }`}>
                {ready ? <CheckIcon /> : <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />}
              </span>
              <span className="font-medium leading-tight">{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5" aria-hidden>
      <path d="M5 12.5l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
